import { Card } from '../../components/ui/Card';
import type { Room, RoomView } from './api';

/**
 * The session numbers (dev/design/GetVibed DJ Console.dc.html).
 *
 * ## Scoped to this broadcast, not to today
 *
 * A host who ran a room this morning and another tonight wants tonight's
 * numbers, and "today" is the wrong unit for anybody broadcasting across
 * midnight. The server scopes every figure here to `live_started_at`.
 *
 * ## What the design shows that this does not
 *
 * The mockup has "avg listen time" and "follows gained by artists". Both are
 * real and computable and simply are not wired yet.
 *
 * The payout panel is absent because boosts carry no money.
 */
export function SessionPanel({ room, view }: { room: Room; view: RoomView }) {
  const { session } = view;

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="meta-caps text-faint">Session</span>
        {room.live_started_at && (
          <span className="font-mono text-[10px] text-ghost">
            since{' '}
            {new Date(room.live_started_at.replace(' ', 'T') + 'Z').toLocaleTimeString(undefined, {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2.5">
        <Figure value={room.listener_count} label="listening now" />
        <Figure value={room.peak_listeners} label="peak listeners" />
        <Figure value={session.tracks_played} label="tracks played" tone="text-accent" />
        <Figure value={session.artists_played} label="artists played" tone="text-fresh" />
      </div>

      <Curve points={view.curve ?? []} />

      <dl className="flex flex-col gap-2 pt-1">
        <Row label="in the queue" value={String(session.queued)} />
        <Row label="boosted" value={String(session.boosted)} tone="text-accent" />
        {/* The single most useful number a host has about their own taste, which
            is why it is here rather than hidden in an analytics page. */}
        <Row
          label="skipped early"
          value={String(session.skipped_early)}
          tone={session.skipped_early > 0 ? 'text-hot-text' : undefined}
        />
      </dl>
    </section>
  );
}

/**
 * Listeners over the last ninety minutes.
 *
 * The one figure on this panel that answers a question a number cannot: not
 * "how many" but "which way". A host watching thirty listeners does not know
 * whether that is the top of a climb or the bottom of a fall, and the whole
 * value of the panel during a broadcast is telling those apart.
 *
 * `stations-sweep.php` writes a sample a minute, so this is empty for the first
 * few minutes of a broadcast and says so. Drawing a plausible shape over no
 * data would be worse than drawing nothing, because it would be believed.
 */
function Curve({ points }: { points: { at: string; listeners: number }[] }) {
  if (points.length < 2) {
    return (
      <Card className="flex flex-col gap-2 p-4">
        <span className="meta-caps text-ghost">Listeners · last 90 min</span>
        <p className="font-mono text-[10px] text-faint">
          A few minutes in, this fills up.
        </p>
      </Card>
    );
  }

  /*
   * Scaled to the window's own peak, not to `peak_listeners`, which is the
   * whole broadcast's. A room that had two hundred an hour ago and forty now
   * would otherwise draw ninety minutes of flat baseline.
   *
   * Floored at 1 so a stretch of zero listeners divides by something.
   */
  const peak = Math.max(1, ...points.map((point) => point.listeners));
  const last = points.length - 1;

  return (
    <Card className="flex flex-col gap-3 p-4">
      <span className="meta-caps text-ghost">Listeners · last 90 min</span>

      <div className="flex h-12 items-end gap-[3px]" data-listener-curve>
        {points.map((point, index) => (
          <span
            key={point.at}
            title={`${point.listeners} at ${time(point.at)}`}
            // The newest bar in the accent: it is the number the host is
            // watching, and it is the one the rest of the shape is context for.
            className={`min-w-px flex-1 rounded-[2px] ${index === last ? 'bg-accent' : 'bg-[#22224a]'}`}
            style={{ height: `${Math.max(4, (point.listeners / peak) * 100)}%` }}
          />
        ))}
      </div>

      <div className="flex justify-between font-mono text-[9px] text-ghost">
        <span>{time(points[0]!.at)}</span>
        <span>now</span>
      </div>
    </Card>
  );
}

/** `22:04` from the server's `Y-m-d H:i:s`, which is UTC. */
function time(at: string): string {
  return new Date(at.replace(' ', 'T') + 'Z').toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function Figure({ value, label, tone = 'text-ink' }: { value: number; label: string; tone?: string }) {
  return (
    <Card className="flex flex-col gap-1 p-3.5">
      <span className={`text-xl font-bold tracking-tight ${tone}`}>{value.toLocaleString()}</span>
      <span className="font-mono text-[9px] text-faint">{label}</span>
    </Card>
  );
}

function Row({ label, value, tone = 'text-ink' }: { label: string; value: string; tone?: string }) {
  return (
    <div className="flex items-center justify-between gap-3 font-mono text-[11px]">
      <dt className="truncate text-faint">{label}</dt>
      <dd className={`shrink-0 ${tone}`}>{value}</dd>
    </div>
  );
}
